"use client";

import Image from "next/image";
import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ease, duration } from "@/lib/motion/easings";

// A real game screenshot with a subtle scroll-linked parallax: the whole framed
// shot drifts vertically (against the copy column) as the section moves through
// the viewport, for depth — plus a one-time entry reveal. The full arena stays
// visible (no crop). Honours prefers-reduced-motion (static, no drift/reveal).
export function ParallaxShot({
  src,
  alt,
  priority = false,
}: {
  src: string;
  alt: string;
  priority?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // gentle drift of the whole card; ~56px total travel across the section transit
  const y = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [28, -28]);

  return (
    <div ref={ref}>
      <motion.div
        initial={reduced ? false : { opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "0px 0px -10% 0px" }}
        transition={{ duration: duration.slow, ease: ease.cinematic }}
      >
        <motion.div style={{ y }} className="will-change-transform">
          <div className="group relative overflow-hidden rounded-lg border border-line-gold bg-elevated shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]">
            <div className="pointer-events-none absolute inset-0 z-10 rounded-lg ring-1 ring-inset ring-gold/10" />
            <Image
              src={src}
              alt={alt}
              width={1600}
              height={1000}
              priority={priority}
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="h-auto w-full"
            />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
